import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BsTrash, BsPlusCircle } from "react-icons/bs";
import { useToast } from "../ToastContext/ToastContext";

const BuildingDetailModal = ({
  show,
  handleClose,
  building,
  onSave,
  isNewBuilding,
}) => {
  const { t } = useTranslation(["usersetting"]);
  const [buildingData, setBuildingData] = useState({});
  const [errors, setErrors] = useState({});
  const [contactPoint, setContactPoint] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
  });
  const [contactPoints, setContactPoints] = useState([]);
  
  useEffect(() => {
    if (!isNewBuilding && building) {
      // Mappiamo i campi del building ai nomi corretti
      setBuildingData({
        ...building,
        address: {
          ...building.address,
          // Non facciamo il mapping qui perché lo facciamo nel form
        },
        contactPoints: building.contactPoints || [] // map contactPoints -> contactPoints
      });
      setContactPoints(building.contactPoints || []);
    } else {
      // Reset completo per un nuovo building
      setBuildingData({
        description: "",
        address: {
          country: "",
          street: "",
          houseNumber: "",
          zipCode: "",
          city: "",
          region: ""
        },
        contactPoints: []
      });
      setContactPoints([]);
    }
  }, [building, isNewBuilding, show]); // Aggiungiamo show per resettare quando si riapre il modal

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setBuildingData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [addressField]: value,
        },
      }));
    } else {
      setBuildingData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleContactPointChange = (index, field, value) => {
    const updatedContactPoints = [...contactPoints];
    updatedContactPoints[index][field] = value;
    setContactPoints(updatedContactPoints);
  };

  const handleAddContactPoint = () => {
    const validationErrors = validateForm(contactPoint, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setContactPoints((prevContactPoints) => [
      ...prevContactPoints,
      {
        name: contactPoint.name,
        surname: contactPoint.surname,
        email: contactPoint.email,
        phone: contactPoint.phone,
      },
    ]);
    setContactPoint({ name: "", surname: "", email: "", phone: "" });
    setErrors({}); // Resetta gli errori
  };

  const handleDeleteContactPoint = (index) => {
    const updatedContactPoints = contactPoints.filter((_, i) => i !== index);
    setContactPoints(updatedContactPoints);
  };

  const handleSave = async () => {
    // Validazione dei dati del building
    const validationErrors = {};
    if (!buildingData.description) {
      validationErrors.description = t(
        "usersetting:validation.descriptionrequired"
      );
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Mappiamo i contactPoints nel formato corretto prima di salvare
    const updatedBuildingData = {
      ...buildingData,
      contactPoints: contactPoints // Usiamo contactPoints invece di contactpoints
    };

    await onSave(updatedBuildingData);
    handleClose();
  };

  // Funzione di validazione separata
  const validateForm = (contactPoint, t) => {
    const validationErrors = {};

    if (!contactPoint.email) {
      validationErrors.email = t("usersetting:validation.email");
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(contactPoint.email)
    ) {
      validationErrors.email = t("usersetting:validation.emailinvalid");
    }

    return validationErrors;
  };

  return (
    <Modal show={show} onHide={handleClose} dialogClassName="building-detail-modal" size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {isNewBuilding
            ? t("usersetting:titles.add")
            : t("usersetting:titles.edit")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t("usersetting:caption.description")}</Form.Label>
            <Form.Control
              type="text"
              name="description"
              value={buildingData?.description || ""}
              onChange={handleChange}
              isInvalid={!!errors.description}
            />
            <Form.Control.Feedback type="invalid">
              {errors.description}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Address fields */}
          <Form.Group className="mb-3">
            <Form.Label>{t("usersetting:caption.address")}</Form.Label>
            <Row>
              <Col sm={6}>
                <Form.Control
                  type="text"
                  name="address.country"
                  placeholder={t("usersetting:caption.nation")}
                  value={buildingData.address?.country || ""}
                  onChange={handleChange}
                  isInvalid={!!errors.country}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.country}
                </Form.Control.Feedback>
              </Col>
              <Col sm={6}>
                <Form.Control
                  type="text"
                  name="address.street"
                  placeholder={t("usersetting:caption.street")}
                  value={buildingData.address?.street || ""}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Form.Control
                  type="text"
                  name="address.houseNumber"
                  placeholder={t("usersetting:caption.housenumber")}
                  value={buildingData.address?.houseNumber || ""}
                  onChange={handleChange}
                />
              </Col>
              <Col sm={6}>
                <Form.Control
                  type="text"
                  name="address.zipCode"
                  placeholder={t("usersetting:caption.zipcode")}
                  value={buildingData.address?.zipCode || ""}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Form.Control
                  type="text"
                  name="address.city"
                  placeholder={t("usersetting:caption.city")}
                  value={buildingData.address?.city || ""}
                  onChange={handleChange}
                />
              </Col>
              <Col sm={6}>
                <Form.Control
                  type="text"
                  name="address.region"
                  placeholder={t("usersetting:caption.region")}
                  value={buildingData.address?.region || ""}
                  onChange={handleChange}
                />
              </Col>
            </Row>
          </Form.Group>

          {/* Contact Points Section */}
          <div className="mt-4">
            <h5>{t("usersetting:caption.contactpoints")}</h5>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>{t("usersetting:caption.name")}</th>
                  <th>{t("usersetting:caption.surname")}</th>
                  <th>{t("usersetting:caption.email")}</th>
                  <th>{t("usersetting:caption.phone")}</th>
                  <th>{t("usersetting:caption.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {contactPoints.map((cp, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Control
                        type="text"
                        value={cp.name || ""}
                        onChange={(e) =>
                          handleContactPointChange(index, "name", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={cp.surname || ""}
                        onChange={(e) =>
                          handleContactPointChange(index, "surname", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="email"
                        value={cp.email || ""}
                        onChange={(e) =>
                          handleContactPointChange(index, "email", e.target.value)
                        }
                        isInvalid={!!errors.email}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={cp.phone || ""}
                        onChange={(e) =>
                          handleContactPointChange(index, "phone", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Button
                        variant="link"
                        className="text-danger"
                        onClick={() => handleDeleteContactPoint(index)}
                      >
                        <BsTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Form per aggiungere un nuovo Contact Point */}
          <Form.Group className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder={t("usersetting:caption.name")}
                  value={contactPoint.name}
                  onChange={(e) =>
                    setContactPoint({ ...contactPoint, name: e.target.value })
                  }
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Col>
              <Col>
                <Form.Control
                  type="text"
                  name="surname"
                  placeholder={t("usersetting:caption.surname")}
                  value={contactPoint.surname}
                  onChange={(e) =>
                    setContactPoint({ ...contactPoint, surname: e.target.value })
                  }
                  isInvalid={!!errors.surname}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.surname}
                </Form.Control.Feedback>
              </Col>
              <Col>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder={t("usersetting:caption.email")}
                  value={contactPoint.email}
                  onChange={(e) =>
                    setContactPoint({ ...contactPoint, email: e.target.value })
                  }
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Col>
              <Col>
                <Form.Control
                  type="text"
                  name="phone"
                  placeholder={t("usersetting:caption.phonenumber")}
                  value={contactPoint.phone}
                  onChange={(e) =>
                    setContactPoint({ ...contactPoint, phone: e.target.value })
                  }
                  isInvalid={!!errors.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              </Col>
              <Col sm="auto">
                <Button variant="success" onClick={handleAddContactPoint}>
                  <BsPlusCircle className="me-1" />
                  {t("usersetting:button.caption.addcontact")}
                </Button>
              </Col>
            </Row>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("common:button.close.caption")}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {t("common:button.save.caption")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BuildingDetailModal;